import {inject, Injectable, signal} from '@angular/core';
import {HttpClient, HttpErrorResponse, HttpHeaders} from '@angular/common/http';
import {catchError, Observable, map} from 'rxjs';
import {GridGetService, GridServiceParams, GridServiceResponse} from 'src/app/shared/model/page-grid';
import {removeEmptyParams} from 'src/infrastructure/helper/remove-empty-params';
import {CommissionReconciliationResponse} from '../models/commission-reconciliation';
import {ExportPdfService} from 'src/app/shared/service/files/export-pdf.service';
import moment from 'moment';

@Injectable({
  providedIn: 'root'
})
export class CommissionReconciliationGridService implements GridGetService<CommissionReconciliationResponse> {
    private readonly http = inject(HttpClient);
    private readonly pdfService = inject(ExportPdfService);

    public readonly processorId = signal<number | undefined>(undefined);

    private getAutocompleteValue(value: any): string {
      if (value && typeof value === 'object' && 'value' in value) {
        return value.value;
      }
      return value || '';
    }

    private getBaseUrl(processorId?: number): string {
      switch (processorId) {
        case 4:
        case undefined:
        case null:
        default:
          return 'dbov2/onyx-data';
        case 5:
          return 'dbov2/tacs-data';
        case 6:
          return 'dbov2/paymode';
      }
    }

    private getConfirmationNumberKey(processorId?: number): string {
      switch (processorId) {
        case 4:
        case 5:
        case undefined:
        case null:
        default:
          return 'hotelConfirmationNumber';
        case 6:
          return 'confirmationNumber';
      }
    }

    public mapSortProperty(sortProperty?: string, processorId?: number): string {
      if (!sortProperty) return '';

      const safeDefault = 'id';
      const prop = sortProperty;

      if (processorId === 5) {
        if (prop === 'grossCommissionDue') return 'reportRevenue';
        if (prop === 'commissionableRevenue' || prop === 'grossCommissionDue') return safeDefault;
        return prop;
      }

      if (processorId === 6) {
        if (prop === 'grossCommissionDue' || prop === 'reportRevenue') return 'commissionAmount';
        if (prop === 'commissionableRevenue') return 'commissionAmount';
        return prop;
      }

      if (prop === 'reportRevenue') return 'grossCommissionDue';
      return prop;
    }

    getItems({filters, page, sort} : GridServiceParams): Observable<GridServiceResponse<CommissionReconciliationResponse>>  {
        this.processorId.set(filters?.['processorId']);

        const apiPropertyName = this.mapSortProperty(sort?.active, filters?.['processorId']);

        const params:Record<string, string | number | boolean> = {
          pageSize:  page?.pageSize || 25,
          pageNumber : (page?.pageIndex || 0) + 1,
          propertyName: apiPropertyName || '',
          orderKey: sort?.direction || '',

          processorId: filters?.['processorId'] ?? '',
          fileName: this.getAutocompleteValue(filters?.['fileName']),
          arrivalDateFrom: filters?.['arrivalDateFrom'] ? moment(filters?.['arrivalDateFrom']).format('YYYY-MM-DD') : '',
          arrivalDateTo: filters?.['arrivalDateTo'] ? moment(filters?.['arrivalDateTo']).format('YYYY-MM-DD') : '',
          departureDateFrom: filters?.['departureDateFrom'] ? moment(filters?.['departureDateFrom']).format('YYYY-MM-DD') : '',
          departureDateTo: filters?.['departureDateTo'] ? moment(filters?.['departureDateTo']).format('YYYY-MM-DD') : '',
          [this.getConfirmationNumberKey(filters?.['processorId'])]: this.getAutocompleteValue(filters?.['hotelConfirmationNumber']),
          adxTripReference: this.getAutocompleteValue(filters?.['adxTripReference']),
          dataSourceId: filters?.['dataSourceId'] ?? '',
        };

        removeEmptyParams(params);

        return this.http.get<GridServiceResponse<CommissionReconciliationResponse>>(`${this.getBaseUrl(filters?.['processorId'])}`, { params: {...params} })
        .pipe(catchError((error:HttpErrorResponse)=>{
            throw new Error(error?.message || 'Server error');
        }));
    }

    getPdf({ filters, page, sort }: GridServiceParams): Observable<void> {

    const apiPropertyName = this.mapSortProperty(sort?.active, filters?.['processorId']);

    const params:Record<string, string | number | boolean> = {
      PageNumber : (page?.pageIndex || 0) + 1,
      PageSize:  page?.pageSize || 100,
      PropertyName: apiPropertyName || '',
      OrderKey: sort?.direction || '',

            exportToPdf: true,
            format: 'pdf',
            formatCurrency: 'true',
            hideCurrencySymbol: 'true',
            currencyCode: 'USD',
            currencySymbol: '$',
            currencyFormat: '0.00',
            processorId: filters?.['processorId'] ?? '',
            fileName: this.getAutocompleteValue(filters?.['fileName']),
            arrivalDateFrom: filters?.['arrivalDateFrom'] ? moment(filters?.['arrivalDateFrom']).format('YYYY-MM-DD') : '',
            arrivalDateTo: filters?.['arrivalDateTo'] ? moment(filters?.['arrivalDateTo']).format('YYYY-MM-DD') : '',
            departureDateFrom: filters?.['departureDateFrom'] ? moment(filters?.['departureDateFrom']).format('YYYY-MM-DD') : '',
            departureDateTo: filters?.['departureDateTo'] ? moment(filters?.['departureDateTo']).format('YYYY-MM-DD') : '',
            hotelConfirmationNumber: this.getAutocompleteValue(filters?.['hotelConfirmationNumber']),
            adxTripReference: this.getAutocompleteValue(filters?.['adxTripReference']),
            dataSourceId: filters?.['dataSourceId'] ?? '',
        };

        removeEmptyParams(params);

        return this.http.get<{ pdfBase64: string }>(this.getBaseUrl(filters?.['processorId']), { params })
        .pipe(
            map((response) => {
              if (response && response.pdfBase64) {
                const timestamp = new Date().toISOString().replace(/[-:T]/g, '').split('.')[0];
                const fileName = `commission-reconciliation_${timestamp}.pdf`;
                this.pdfService.savePdfFromBase64(response.pdfBase64, fileName);
              } else {
                console.error('Invalid PDF response:', response);
              }
              return;
            }),
            catchError((error: HttpErrorResponse) => {
              console.error('Error downloading PDF:', error);
              throw new Error(error.error);
            })
          );

    }

    public uploadFile(processorId: number, file: File) {
      const headers = new HttpHeaders({
        'Content-Type': 'multipart/form-data',
      });
      const getKey = () => {
        if (processorId === 5 || processorId === 6) return 'File';
        return 'ZipFile';
      };
      const form = new FormData();
      form.append(getKey(), file);
      return this.http.post<void>(this.getBaseUrl(processorId), form, { headers });
    }
}
